import { Role, getRoleCode } from "constant/Role"
import { adminRoutes, teacherRoutes, lxoRoutes, procurementManagerRoutes } from "routes"
import { buildLxMenu } from "./lxMenu"
import { buildCsoMenu } from "./csoMenu"
import { buildMktMenu } from "./mktMenu"
import { buildCenterAdminMenu } from "./centerAdminMenu"
import { buildManagementMenu } from "./managementMenu"
import { buildCallCentreMenu } from "./callCentreMenu"
import { buildCdfmMenu } from "./cdfmMenu"
import { buildCentreManagerMenu } from "./centreManagerMenu"
import { buildHseMenu } from "./hseMenu"
import { buildItMenu } from "./itMenu"
import { buildLegalMenu } from "./legalMenu"
import { buildTalentDevelopmentMenu } from "./talentDevelopmentMenu"
import { buildTeacherTrainingMenu } from "./teacherTrainingMenu"
import { buildAeMenu } from "./aeMenu"
import { buildCorpMenu } from "./corpMenu"
import { buildProcurementMenu } from "./procurementMenu"
import { filterLeaveRequestForNonManager } from "./helpers"

// LX is built once from its own module (a clone of the LXO menu).
const lxMenu = buildLxMenu()

// Back-office roles: each gets its own cloned menu instance so future
// per-role overrides in the individual menu files never affect the others.
const csoMenu = buildCsoMenu()
const mktMenu = buildMktMenu()
const centerAdminMenu = buildCenterAdminMenu()

// Department roles: one entry per department in the org chart, each with its own
// cloned instance of the staff (LXO-derived) menu.
const managementMenu = buildManagementMenu()
const callCentreMenu = buildCallCentreMenu()
const cdfmMenu = buildCdfmMenu()
const centreManagerMenu = buildCentreManagerMenu()
const hseMenu = buildHseMenu()
const itMenu = buildItMenu()
const legalMenu = buildLegalMenu()
const talentDevelopmentMenu = buildTalentDevelopmentMenu()
const teacherTrainingMenu = buildTeacherTrainingMenu()
const aeMenu = buildAeMenu()
const corpMenu = buildCorpMenu()
const procurementMenu = buildProcurementMenu()

/**
 * Single source of truth mapping a role CODE to its menu + landing route.
 *
 * Adding a new role later = create src/menus/<role>Menu.js (clone an existing
 * base, apply overrides) and add one entry here. No changes needed in App.js
 * or the Sidenav role switcher.
 *
 * Entry shape:
 *   menu            : the route/menu array for the role
 *   defaultRoute    : where the role lands by default ("*" navigates here)
 *   transformForUser: optional (menu, ctx) => menu, applied per-user at runtime
 *   needsManagerCheck: optional, true to trigger the isManager lookup in App.js
 */
export const roleMenuRegistry = {
  [Role.SUPPER_ADMIN]: {
    menu: adminRoutes,
    defaultRoute: "/user-management/admin-management",
  },
  [Role.ADMIN]: {
    menu: adminRoutes,
    defaultRoute: "/user-management/admin-management",
  },
  // Keep each back-office role as a distinct registry entry so FE role labels,
  // role switcher text, and page-level role display continue to reflect the
  // actual active role, even when multiple roles share the same base menu.
  // These now clone the LXO-derived staff menu (not adminRoutes) — only
  // SUPPER_ADMIN/ADMIN keep the admin UI.
  [Role.CSO]: {
    menu: csoMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.MKT]: {
    menu: mktMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.CENTER_ADMIN]: {
    menu: centerAdminMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  // Sub-role of CENTER_ADMIN: same menu, backend uses it only to grant extra permissions.
  [Role.EC_SM]: {
    menu: centerAdminMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  // Finance's menu base is the full Procurement Manager tree, but
  // accessibleRoutes.js scopes it down to just Budget Control + the Payment
  // group (Approve Payment Request only) + Suppliers +
  // Items via PROCUREMENT_MENU_ACCESS[ROLE_FINANCE] (menuAccess.js) — Finance
  // has no Order/Purchase approval access, so defaultRoute must land
  // somewhere still in that scoped set (/procurement/approvals isn't).
  [Role.FINANCE]: {
    menu: procurementManagerRoutes,
    defaultRoute: "/procurement/payment-requests/approvals",
  },
  [Role.TEACHER]: {
    menu: teacherRoutes,
    defaultRoute: "/",
  },
  [Role.LXO]: {
    menu: lxoRoutes,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.LX]: {
    menu: lxMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.SALE]: {
    menu: lxoRoutes,
    defaultRoute: "/",
  },
  [Role.PROCUREMENT_MANAGER]: {
    menu: procurementManagerRoutes,
    defaultRoute: "/procurement/approvals",
  },
  // Department roles. They share the staff base, which carries the
  // "leave-request" item, so they use the same manager gating as LXO/LX.
  [Role.MANAGEMENT]: {
    menu: managementMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.CALL_CENTRE]: {
    menu: callCentreMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.CDFM]: {
    menu: cdfmMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.CENTRE_MANAGER]: {
    menu: centreManagerMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.HSE]: {
    menu: hseMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.IT]: {
    menu: itMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.LEGAL]: {
    menu: legalMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.TALENT_DEVELOPMENT]: {
    menu: talentDevelopmentMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.TEACHER_TRAINING]: {
    menu: teacherTrainingMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.AE]: {
    menu: aeMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.CORP]: {
    menu: corpMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  // accessibleRoutes.js appends the filtered Procurement Manager tree onto
  // this base menu (PROCUREMENT_MENU_ACCESS[ROLE_PROCUREMENT].tree ===
  // "pm-append" in menuAccess.js), so this entry only needs to supply the
  // base menu (Home/Calendar/Assistant).
  [Role.PROCUREMENT]: {
    menu: procurementMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  // Sub-role of PROCUREMENT: same menu, backend uses it only to grant extra permissions.
  [Role.PROCUREMENT_STAFF]: {
    menu: procurementMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
  [Role.REQUESTER]: {
    menu: procurementMenu,
    defaultRoute: "/",
    transformForUser: filterLeaveRequestForNonManager,
    needsManagerCheck: true,
  },
}

/** Resolve the registry entry for a role (accepts a role code or label). */
export const getRoleMenu = (role) => roleMenuRegistry[getRoleCode(role)] || null

/**
 * The subset of a user's JWT roles that the registry actually supports, in a
 * stable order. Used to populate the role switcher; a length > 1 means the user
 * can switch roles.
 */
export const getSwitchableRoles = (roles = []) =>
  Object.keys(roleMenuRegistry).filter((code) => roles.includes(code))

/** Redux selector form of getSwitchableRoles, sourced from the JWT role list. */
export const selectSwitchableRoles = (state) =>
  getSwitchableRoles(state.auth?.roles || [])
